import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Animated,
  Platform,
} from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { AdminColors } from '../../../constants/adminColors';
import FontFamily from '../../../../../constants/fonts';

const EditIcon = () => (
  <Svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <Path
      d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"
      stroke={AdminColors.blue900}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <Path
      d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"
      stroke={AdminColors.blue900}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

const CloseIcon = () => (
  <Svg width="13" height="13" viewBox="0 0 24 24" fill="none">
    <Path
      d="M18 6L6 18M6 6l12 12"
      stroke={AdminColors.textSecondary}
      strokeWidth="2"
      strokeLinecap="round"
    />
  </Svg>
);

interface SelectionBarProps {
  selectedCount: number;
  canEdit?: boolean;
  onEdit: () => void;
  onBulkEdit: () => void;
  onClear: () => void;
}

const SelectionBar: React.FC<SelectionBarProps> = ({
  selectedCount,
  canEdit = true,
  onEdit,
  onBulkEdit,
  onClear,
}) => {
  const isOpen = selectedCount > 0;
  const isBulk = selectedCount > 1;

  if (!isOpen) return null;

  return (
    <View style={styles.bar}>
      {/* Selection count */}
      <View style={styles.left}>
        <Text style={styles.countTxt}>
          <Text style={styles.countNum}>{selectedCount}</Text>
          {' '}location{selectedCount !== 1 ? 's' : ''} selected
        </Text>
      </View>

      {/* Actions */}
      <View style={styles.actions}>
        {canEdit && (
          <>
            {isBulk ? (
              // Bulk edit button
              <TouchableOpacity style={styles.editBtn} onPress={onBulkEdit} activeOpacity={0.7}>
                <EditIcon />
                <Text style={styles.editTxt}>Bulk Edit</Text>
              </TouchableOpacity>
            ) : (
              // Single edit button
              <TouchableOpacity style={styles.editBtn} onPress={onEdit} activeOpacity={0.7}>
                <EditIcon />
                <Text style={styles.editTxt}>Edit</Text>
              </TouchableOpacity>
            )}

            {/* Clear selection */}
            <View style={styles.divider} />
          </>
        )}
        <TouchableOpacity style={styles.clearBtn} onPress={onClear} activeOpacity={0.7}>
          <CloseIcon />
          <Text style={styles.clearTxt}>Clear selection</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  bar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 15,
    paddingVertical: 10,
    backgroundColor: '#EAF4FF',
    borderBottomWidth: 1,
    borderBottomColor: '#B8D7FB',
  },
  left: {
    flex: 1,
  },
  countTxt: {
    fontSize: 12,
    color: AdminColors.textSecondary,
    fontFamily: FontFamily.regular,
  },
  countNum: {
    fontWeight: '700',
    color: AdminColors.blue900,
    fontFamily: FontFamily.bold,
  },
  actions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  editBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    height: 30,
    paddingHorizontal: 12,
    backgroundColor: AdminColors.bgSurface,
    borderWidth: 1,
    borderColor: '#B8D7FB',
    borderRadius: 6,
  },
  editTxt: {
    fontSize: 11,
    fontWeight: '700',
    color: AdminColors.blue900,
    fontFamily: FontFamily.bold,
  },
  divider: {
    width: 1,
    height: 18,
    backgroundColor: '#B8D7FB',
  },
  clearBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    height: 30,
    paddingHorizontal: 8,
  },
  clearTxt: {
    fontSize: 11,
    fontWeight: '600',
    color: AdminColors.textSecondary,
    fontFamily: FontFamily.medium,
  },
});

export default SelectionBar;
